import {
  MarketplaceBankingSubProductTypeEnum,
  MarketplaceEntityTypeEnum,
} from '@repo/supergraph/marketplace/enums';
import type { MarketplaceEntityTypeWithDriversAndCtaOverride } from '@repo/supergraph/marketplace/types';
import type { ProductCard } from '@repo/supergraph/roundup/base/types';
import { StructuredContentDocument } from '@repo/supergraph/structuredContent/types';
import { TERM_LENGTH_FORMAT } from '@repo/utils/cdTerm/constants';
import { formatLabel } from '@repo/utils/cdTerm/formatLabel';
import type { Graph, Product } from 'schema-dts';
import { ORGANIZATION_ID } from '../constants';
import { extractStructuredContent } from '../utils';

type GetBankingProductSchemaArgs = {
  entity: MarketplaceEntityTypeWithDriversAndCtaOverride;
  url: string;
  pros?: StructuredContentDocument[];
  cons?: StructuredContentDocument[];
};

const getProductSchema = ({
  entity,
  url,
  category,
  description,
  pros,
  cons,
}: {
  entity: ProductCard['marketplaceEntity'];
  url: string;
  category: string;
  description: string;
  pros?: StructuredContentDocument[];
  cons?: StructuredContentDocument[];
}): Product => {
  const ratingValue = entity.starRating;
  return {
    '@type': 'Product',
    name: entity.displayName,
    image: entity.image?.source,
    url: `${url}#${entity.id}`,
    identifier: entity.id,
    category,
    description,
    ...(ratingValue && {
      review: {
        '@type': 'Review',
        author: {
          '@id': ORGANIZATION_ID,
        },
        reviewRating: {
          '@type': 'Rating',
          ratingValue,
          bestRating: 5,
        },
        ...(pros && {
          positiveNotes: {
            '@type': 'ItemList',
            itemListElement: pros.map((pro: StructuredContentDocument, idx: number) => ({
              '@type': 'ListItem',
              position: idx,
              name: extractStructuredContent(pro),
            })),
          },
        }),
        ...(cons && {
          negativeNotes: {
            '@type': 'ItemList',
            itemListElement: cons.map((con: StructuredContentDocument, idx: number) => ({
              '@type': 'ListItem',
              position: idx,
              name: extractStructuredContent(con),
            })),
          },
        }),
      },
    }),
  };
};

const getBankingProductSchema = ({
  entity,
  url,
  pros,
  cons,
}: GetBankingProductSchemaArgs): Product => {
  if (entity.entityType === MarketplaceEntityTypeEnum.Institutions) {
    const category = entity.institutionType
      ? entity.institutionType
          .split('_')
          .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
          .join(' ')
      : 'Banking institution';

    return getProductSchema({
      entity,
      url,
      category,
      description: `${entity.displayName} - ${category}`,
    });
  }

  let category = entity.subProductTypeLabel || 'Banking account';
  let description = `${entity.displayName} - ${category}`;

  if (entity.subProductType === MarketplaceBankingSubProductTypeEnum.CD) {
    const certificateLabel =
      entity.product?.institution?.institutionType === 'credit_union'
        ? 'Share Certificates'
        : 'Certificates of Deposit';
    const termLength = formatLabel({
      cdDepositTerm: entity.cdDepositTerm,
      format: TERM_LENGTH_FORMAT.hyphen,
      displayName: entity.displayName,
    });

    category = certificateLabel;
    description = `${entity.displayName} - ${certificateLabel} - ${termLength ?? ''}`;
  }

  return getProductSchema({
    entity,
    url,
    category,
    description,
    pros,
    cons,
  });
};

export const getProductListSchema = ({
  offers,
  url,
}: {
  offers: ProductCard[];
  url: string;
}): Graph => {
  const products = offers.map(offer =>
    getBankingProductSchema({
      entity: offer.marketplaceEntity,
      url,
      pros: offer.pros,
      cons: offer.cons,
    }),
  );

  return {
    '@context': 'https://schema.org',
    '@graph': products,
  };
};
